import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Button, Paper, Switch, FormControlLabel,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
  Chip, MenuItem, Checkbox, ListItemText, OutlinedInput, Select,
  FormControl, InputLabel, Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

const MAQUINAS_DISPONIBLES = [
  'Machine1','Machine2','Machine3','Machine4','Machine5','Machine6','Machine7',
  'Machine8','Machine9','Machine10','Machine11',
];

const initialForm = {
  nombre: '',
  maquinas: [],
  cargo: '',
  operariosPorTurno: 1,
  turnosActivos: [],
  activo: true,
  notas: '',
};

const ReglasProgramacion = () => {
  const [form, setForm]           = useState(initialForm);
  const [reglas, setReglas]       = useState([]);
  const [cargos, setCargos]       = useState([]);
  const [editId, setEditId]       = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [deleteId, setDeleteId]   = useState(null);
  const [error, setError]         = useState('');

  useEffect(() => {
    fetchReglas();
    fetchCargos();
  }, []);

  const token = () => localStorage.getItem('token');

  const fetchReglas = async () => {
    try {
      const res = await fetch('/api/reglas-programacion', {
        credentials: 'include',
        headers: { 'x-access-token': token() },
      });
      const data = await res.json();
      setReglas(Array.isArray(data) ? data : []);
    } catch (err) { setReglas([]); }
  };

  const fetchCargos = async () => {
    try {
      const res = await fetch('/api/cargos', {
        credentials: 'include',
        headers: { 'x-access-token': token() },
      });
      const data = await res.json();
      setCargos(Array.isArray(data) ? data : []);
    } catch (err) { setCargos([]); }
  };

  const handleChange = (e) => {
    const { name, value, checked, type } = e.target;
    setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setError('');
  };

  const handleMaquinasChange = (e) => {
    setForm(prev => ({ ...prev, maquinas: e.target.value }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre.trim() || !form.maquinas.length || !form.cargo || !form.operariosPorTurno) {
      setError('Nombre, máquinas, cargo y operarios por turno son obligatorios');
      return;
    }
    const url    = editId ? `/api/reglas-programacion/${editId}` : '/api/reglas-programacion';
    const method = editId ? 'PUT' : 'POST';
    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', 'x-access-token': token() },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (res.ok) {
        setForm(initialForm);
        setEditId(null);
        fetchReglas();
      } else {
        setError(data.message || 'Error al guardar');
      }
    } catch (err) { setError('Error de conexión'); }
  };

  const handleEdit = (r) => {
    setForm({
      nombre: r.nombre,
      maquinas: r.maquinas,
      cargo: r.cargo,
      operariosPorTurno: r.operariosPorTurno,
      turnosActivos: r.turnosActivos || [],
      activo: r.activo,
      notas: r.notas || '',
    });
    setEditId(r._id);
    setError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCancelEdit = () => { setForm(initialForm); setEditId(null); setError(''); };

  const confirmDelete = (id) => { setDeleteId(id); setOpenDialog(true); };

  const handleDelete = async () => {
    try {
      await fetch(`/api/reglas-programacion/${deleteId}`, {
        method: 'DELETE',
        headers: { 'x-access-token': token() },
        credentials: 'include',
      });
    } catch (err) { console.error(err); }
    setOpenDialog(false);
    setDeleteId(null);
    fetchReglas();
  };

  const toggleActivo = async (r) => {
    try {
      await fetch(`/api/reglas-programacion/${r._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'x-access-token': token() },
        credentials: 'include',
        body: JSON.stringify({ ...r, activo: !r.activo }),
      });
      fetchReglas();
    } catch (err) { console.error(err); }
  };

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, px: 2 }}>
      <Typography variant="h5" sx={{ mb: 3, fontWeight: 600 }}>
        Reglas de Programación
      </Typography>

      {/* Formulario */}
      <Paper sx={{ p: 3, mb: 4 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          {editId ? 'Editar Regla' : 'Nueva Regla'}
        </Typography>
        <Box component="form" onSubmit={handleSubmit}>
          <TextField
            label="Nombre de la regla"
            name="nombre"
            value={form.nombre}
            onChange={handleChange}
            fullWidth
            sx={{ mb: 2 }}
            placeholder="Ej: Tejedores Máquinas 1-7"
          />

          {/* Selector múltiple de máquinas */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>Máquinas</InputLabel>
            <Select
              multiple
              name="maquinas"
              value={form.maquinas}
              onChange={handleMaquinasChange}
              input={<OutlinedInput label="Máquinas" />}
              renderValue={(selected) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {selected.map((m) => <Chip key={m} label={m} size="small" />)}
                </Box>
              )}
            >
              {MAQUINAS_DISPONIBLES.map((m) => (
                <MenuItem key={m} value={m}>
                  <Checkbox checked={form.maquinas.includes(m)} />
                  <ListItemText primary={m} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {/* Cargo */}
          <TextField
            select
            label="Cargo requerido"
            name="cargo"
            value={form.cargo}
            onChange={handleChange}
            fullWidth
            sx={{ mb: 2 }}
          >
            <MenuItem value="">-- Selecciona un cargo --</MenuItem>
            {cargos.map((c) => (
              <MenuItem key={c._id} value={c.cargo}>{c.cargo}</MenuItem>
            ))}
          </TextField>

          {/* Operarios por turno */}
          <TextField
            label="Operarios por turno"
            name="operariosPorTurno"
            type="number"
            value={form.operariosPorTurno}
            onChange={handleChange}
            inputProps={{ min: 1, max: 10 }}
            sx={{ mb: 2, width: 220 }}
          />

          {/* Turnos activos */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" sx={{ mb: 0.5, color: 'text.secondary' }}>
              Turnos en que operan estas máquinas (vacío = todos los turnos disponibles)
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {[1, 2, 3].map(n => {
                const activo = form.turnosActivos.includes(n);
                return (
                  <Chip
                    key={n}
                    label={`T${n}`}
                    clickable
                    color={activo ? 'primary' : 'default'}
                    variant={activo ? 'filled' : 'outlined'}
                    onClick={() => {
                      setForm(prev => ({
                        ...prev,
                        turnosActivos: activo
                          ? prev.turnosActivos.filter(t => t !== n)
                          : [...prev.turnosActivos, n].sort(),
                      }));
                    }}
                  />
                );
              })}
            </Box>
          </Box>

          {/* Notas */}
          <TextField
            label="Notas (opcional)"
            name="notas"
            value={form.notas}
            onChange={handleChange}
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 2 }}
          />

          {/* Activo */}
          <FormControlLabel
            control={
              <Switch
                name="activo"
                checked={form.activo}
                onChange={(e) => setForm(prev => ({ ...prev, activo: e.target.checked }))}
                color="success"
              />
            }
            label="Regla activa"
            sx={{ mb: 2, display: 'block' }}
          />

          {error && (
            <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>
          )}

          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button type="submit" variant="contained">
              {editId ? 'Actualizar' : 'Guardar'}
            </Button>
            {editId && (
              <Button variant="outlined" onClick={handleCancelEdit}>Cancelar</Button>
            )}
          </Box>
        </Box>
      </Paper>

      {/* Tabla */}
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 700 }}>Nombre</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Máquinas</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Cargo</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Op/Turno</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Turnos</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Activa</TableCell>
              <TableCell sx={{ fontWeight: 700 }}>Notas</TableCell>
              <TableCell align="center" sx={{ fontWeight: 700 }}>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {reglas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ color: '#888' }}>
                  No hay reglas configuradas
                </TableCell>
              </TableRow>
            ) : (
              reglas.map((r) => (
                <TableRow key={r._id} hover>
                  <TableCell>{r.nombre}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {r.maquinas.map(m => (
                        <Chip key={m} label={m} size="small" variant="outlined" />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell>{r.cargo}</TableCell>
                  <TableCell align="center">{r.operariosPorTurno}</TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', gap: 0.4, justifyContent: 'center', flexWrap: 'wrap' }}>
                      {(r.turnosActivos?.length ? r.turnosActivos : [1, 2, 3]).map(t => (
                        <Chip key={t} label={`T${t}`} size="small" color="primary" variant="outlined" />
                      ))}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Tooltip title={r.activo ? 'Desactivar' : 'Activar'}>
                      <IconButton size="small" onClick={() => toggleActivo(r)}>
                        {r.activo
                          ? <CheckCircleIcon fontSize="small" color="success" />
                          : <CancelIcon fontSize="small" color="disabled" />}
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={{ maxWidth: 180, fontSize: 12, color: '#888' }}>
                    {r.notas || '—'}
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleEdit(r)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => confirmDelete(r._id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Diálogo confirmar eliminación */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>Confirmar eliminación</DialogTitle>
        <DialogContent>
          <Typography>¿Estás seguro de que deseas eliminar esta regla?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>Eliminar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReglasProgramacion;
